export const MIN_OVERVIEW_RESPONSES = 5;
export const MIN_BODYMAP_RESPONSES = 1;

export function getMinResponsesForQuestionType(questionType) {
  return questionType === "bodymap" ? MIN_BODYMAP_RESPONSES : MIN_OVERVIEW_RESPONSES;
}

export function countRpeAnswers(rows = [], allowedPlayerIds = null) {
  const allowed = allowedPlayerIds instanceof Set ? allowedPlayerIds : null;
  return (rows || []).filter((row) => {
    if (allowed && !allowed.has(String(row.player_id))) return false;
    const value = Number(row?.rpe_value);
    return Number.isFinite(value);
  }).length;
}

export function countSubjectiveRespondents(responses = [], allowedPlayerIds = null) {
  const allowed = allowedPlayerIds instanceof Set ? allowedPlayerIds : null;
  const seen = new Set();

  (responses || []).forEach((response) => {
    const playerId = String(response?.player_id || "");
    if (!playerId || (allowed && !allowed.has(playerId))) return;
    const answers = Array.isArray(response?.answers) ? response.answers : [];
    const hasAny = answers.some((entry) => {
      const value = entry?.answer;
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    });
    if (hasAny) seen.add(playerId);
  });

  return seen.size;
}

export function countQuestionRespondents(responses = [], questionId, allowedPlayerIds = null) {
  const allowed = allowedPlayerIds instanceof Set ? allowedPlayerIds : null;
  let count = 0;

  (responses || []).forEach((response) => {
    const playerId = String(response?.player_id || "");
    if (!playerId || (allowed && !allowed.has(playerId))) return;
    const answers = Array.isArray(response?.answers) ? response.answers : [];
    const answer = answers.find((entry) => String(entry.question_id) === String(questionId));
    const value = answer?.answer;
    if (value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === "string" && value.trim() === "") return;
    count += 1;
  });

  return count;
}

export function countRpeHistoryDayResponses(entry, allowedPlayerIds = null) {
  if (Array.isArray(entry?.sessions) && entry.sessions.length > 0) {
    return entry.sessions.reduce(
      (sum, session) => sum + (Number(session?.responses_count) || 0),
      0
    );
  }
  return countRpeAnswers(entry?.rows || [], allowedPlayerIds);
}

export function getReadyDatesFromRpeHistory(history = [], allowedPlayerIds, minRequired, excludeDate = null) {
  return (history || [])
    .map((entry) => ({
      date: entry.date,
      count: countRpeHistoryDayResponses(entry, allowedPlayerIds),
    }))
    .filter((entry) => entry.count >= minRequired && entry.date && entry.date !== excludeDate)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, 5);
}

export function getReadyDatesFromSubjectiveHistory(history = [], allowedPlayerIds, minRequired, excludeDate = null) {
  return (history || [])
    .map((entry) => ({
      date: entry.date,
      count: countSubjectiveRespondents(entry.responses || [], allowedPlayerIds),
    }))
    .filter((entry) => entry.count >= minRequired && entry.date && entry.date !== excludeDate)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, 5);
}

export function getReadyDatesForQuestionHistory(
  history = [],
  questionId,
  allowedPlayerIds,
  minRequired,
  excludeDate = null
) {
  return (history || [])
    .map((entry) => ({
      date: entry.date,
      count: countQuestionRespondents(entry.responses || [], questionId, allowedPlayerIds),
    }))
    .filter((entry) => entry.count >= minRequired && entry.date && entry.date !== excludeDate)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, 5);
}

export function formatOverviewDayLabel(isoDate, locale = "fr-FR") {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(isoDate);
  return date.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
}

export function responsesRemaining(answerCount, minRequired) {
  return Math.max(0, minRequired - (answerCount || 0));
}

export function hasEnoughResponses(answerCount, minRequired) {
  return (answerCount || 0) >= minRequired;
}

export function getOverviewMinRequired({
  minRequired = MIN_OVERVIEW_RESPONSES,
  isAtRiskMode = false,
  scopedPlayerCount = 0,
}) {
  if (!isAtRiskMode || scopedPlayerCount <= 0) return minRequired;
  return Math.min(minRequired, scopedPlayerCount);
}

/**
 * At-risk scope often contains a single player. Show overview when at least one
 * visible at-risk player has answered, without requiring 5 scoped responses.
 */
export function canShowOverview({
  scopedAnswerCount = 0,
  globalAnswerCount = 0,
  minRequired = MIN_OVERVIEW_RESPONSES,
  isAtRiskMode = false,
  scopedPlayerCount = 0,
}) {
  const effectiveMin = getOverviewMinRequired({
    minRequired,
    isAtRiskMode,
    scopedPlayerCount,
  });

  if (hasEnoughResponses(scopedAnswerCount, effectiveMin)) return true;

  if (isAtRiskMode && scopedAnswerCount > 0) return true;

  if (
    !isAtRiskMode &&
    hasEnoughResponses(globalAnswerCount, minRequired) &&
    scopedAnswerCount > 0
  ) {
    return true;
  }

  return false;
}

/**
 * Global squad has enough responses to show indicators, but the at-risk
 * scope does not — suggest switching to global mode.
 */
export function shouldSuggestGlobalMode({
  scopedAnswerCount = 0,
  globalAnswerCount = 0,
  minRequired = MIN_OVERVIEW_RESPONSES,
  isAtRiskMode = false,
}) {
  if (!isAtRiskMode) return false;
  if (!hasEnoughResponses(globalAnswerCount, minRequired)) return false;
  return !hasEnoughResponses(scopedAnswerCount, minRequired);
}
